import { useCallback, useEffect, useRef, useState } from "react";
import { useTerms, useUser } from "../../hooks/useApi";
import Loader from "../basic/Loader";
import Modal from "../layout/Modal";
import Button from "../basic/Button";
import { EVENT_TYPES, reportEvent } from "../../services/eventEmitter";

import "../../styles/terms.scss";
import "../../styles/terms-johnnie-music.scss";

// Hardcoded: the johnnie-music radio (radioid 2025) gets the Red Soul task theme.
const JOHNNIE_MUSIC_NAMESEO = "johnnie-music";
const JOHNNIE_MUSIC_RADIOID = "2025";

const normalizeText = (value) => {
  if (typeof value !== "string") return null;

  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized === "" ? null : normalized;
};

const serializeFormMetadata = (form) => {
  if (!Array.isArray(form)) return [];

  return form.map(({ label, input, small, link }) => {
    let labelText = normalizeText(label?.textContent ?? "");
    const linkText = normalizeText(link?.textContent ?? null);

    if (linkText) {
      labelText = normalizeText(labelText?.replace(linkText, "") ?? "");
    }

    return {
      name: input?.name ?? null,
      type: input?.type ?? input?.tagName?.toLowerCase?.() ?? null,
      label: labelText || null,
      required: !!input?.required,
      small: normalizeText(small?.textContent ?? null),
      link: link
        ? {
            href: link?.href ?? null,
            text: linkText,
          }
        : null,
    };
  });
};

export default function TermsAndConditionsModal({
  nameseo,
  isPeeking,
  setIsPeeking,
}) {
  const { user } = useUser();
  const { extraQuery, data, isLoading, acceptTerms, radioid, radioData } =
    useTerms(nameseo);
  const [ticked, setTicked] = useState(false);
  const [form, setForm] = useState(null);
  const [checked, setChecked] = useState([]);
  const [formValues, setFormValues] = useState({});
  const isSubmittingRef = useRef(false);

  const isBusinessSuccess = (result) => {
    if (!result) return false;
    if (typeof result?.status === "string") {
      return result.status === "success";
    }
    return true;
  };

  const handleAcceptTerms = () => {
    if (isSubmittingRef.current || acceptTerms.isLoading) {
      return;
    }

    console.log("accepting terms");
    isSubmittingRef.current = true;
    const selectedExtra = form
      ? Array.from(new Set(["terms", ...checked]))
      : ["terms"];
    const variables = {
      extra: selectedExtra,
      formMetadata: serializeFormMetadata(form),
      formValues,
      isCustomForm: !!form,
    };

    acceptTerms.mutate(variables, {
      onSuccess: (result) => {
        extraQuery.refetch();

        if (!isBusinessSuccess(result)) {
          return result;
        }

        const selectedOptions = Array.isArray(variables.extra)
          ? variables.extra
          : [variables.extra].filter(Boolean);
        const eventType =
          variables.isCustomForm || variables.formMetadata.length > 0
            ? EVENT_TYPES.CUSTOM_TERMS_CONFIRMED
            : EVENT_TYPES.TERMS_CONFIRMED;

        void reportEvent({
          type: eventType,
          user,
          radio: {
            radioid,
            radioname: radioData?.name,
            nameseo,
          },
          payload: {
            selectedOptions,
            form: variables.formMetadata,
            values: variables.formValues,
            response: result,
            interactionSource: "terms_modal",
          },
        });

        return result;
      },
      onSettled: () => {
        isSubmittingRef.current = false;
      },
    });
  };

  const handleClosePeeking = () => {
    setIsPeeking(false);
  };

  const handleFormChange = useCallback((event) => {
    const { name, type, checked: isChecked, value } = event.target;
    if (!name) return;

    if (type === "checkbox") {
      setChecked((checked) => {
        if (isChecked) {
          return checked.includes(name) ? checked : [...checked, name];
        }
        return checked.filter((checkedName) => checkedName !== name);
      });
      setFormValues((values) => ({
        ...values,
        [name]: isChecked,
      }));
    } else {
      setFormValues((values) => ({
        ...values,
        [name]: value,
      }));
    }
  }, []);

  useEffect(() => {
    if (form) {
      let acceptAll = true;
      form.forEach(({ input }) => {
        if (!input?.required) return;

        const inputName = input.name;
        if (input?.type === "checkbox") {
          if (!checked.includes(inputName)) {
            acceptAll = false;
          }
          return;
        }

        if (!formValues[inputName]) {
          acceptAll = false;
        }
      });
      setTicked(acceptAll);
    }
  }, [checked, form, formValues]);

  useEffect(() => {
    if (data?.text) {
      const dom = document.createElement("div");
      dom.innerHTML = data.text;
      const _form = dom.querySelector("form");
      console.log("has form?", _form);
      if (_form) {
        const formGroups = _form.querySelectorAll(".form-group");
        const components = [];
        formGroups.forEach((formGroup) => {
          const label = formGroup.querySelector("label");
          const input = formGroup.querySelector("input, select, textarea");
          const small = formGroup.querySelector("small");
          const link = label ? label.querySelector("a") : null;
          components.push({
            label,
            input,
            small,
            link,
          });
        });
        setForm(components);
      } else {
        setForm(null);
      }
    }
  }, [data]);

  const isJohnnieMusic =
    nameseo === JOHNNIE_MUSIC_NAMESEO ||
    String(radioid ?? "") === JOHNNIE_MUSIC_RADIOID;

  if (isLoading) return <Loader />;
  if (data) {
    return (
      <Modal
        disableClose={true}
        className={
          isJohnnieMusic
            ? "terms-modal terms-modal-johnnie-music"
            : "terms-modal"
        }
      >
        {form ? (
          <div
            className="terms-container"
            style={{
              height:
                Math.max(20, Math.min(60, data.text.length * 0.08)) + "vh",
            }}
          >
            {form.map((formGroup, index) => (
              <FormGroup
                key={index}
                onChange={handleFormChange}
                {...formGroup}
                isPeeking={isPeeking}
              />
            ))}
          </div>
        ) : (
          <div
            dangerouslySetInnerHTML={{ __html: data.text }}
            className="terms-container"
            style={{
              height:
                Math.max(20, Math.min(60, data.text.length * 0.08)) + "vh",
            }}
          />
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {isPeeking ? (
            <Button
              onClick={handleClosePeeking}
              className="success"
              style={{ marginLeft: 10 }}
            >
              Cerrar
            </Button>
          ) : (
            <>
              {!form && (
                <input
                  type="checkbox"
                  checked={ticked}
                  onChange={(event) => setTicked((ticked) => !ticked)}
                  style={{ width: 30, height: 30 }}
                  disabled={!!form}
                />
              )}
              <Button
                onClick={handleAcceptTerms}
                className="success"
                disabled={!ticked || acceptTerms.isLoading}
                style={{ marginLeft: 10 }}
              >
                {acceptTerms?.isLoading ? <Loader /> : "Autorizar"}
              </Button>
            </>
          )}
        </div>
      </Modal>
    );
  }
  return null;
}

function FormGroup({ label, input, small, link, onChange, isPeeking }) {
  let labelText = label?.textContent;
  const inputName = input?.name;
  const inputType = input?.type ?? "text";
  const smallText = small?.textContent;
  const linkHref = link?.href;
  const linkText = link?.textContent;
  const required = !!input?.required;

  if (link && linkHref && linkText) {
    labelText = labelText.replace(linkText, "");
  }

  if (isPeeking) {
    return (
      link && (
        <div
          className="form-group"
          style={{ display: "flex", marginBottom: 10 }}
        >
          <a
            href={linkHref}
            target="_blank"
            rel="noreferrer"
            style={{ whiteSpace: "pre" }}
          >
            {linkText}
          </a>
        </div>
      )
    );
  }

  return (
    <div className="form-group" style={{ display: "flex", marginBottom: 10 }}>
      <input
        type={inputType}
        onChange={onChange}
        name={inputName}
        required={required}
        placeholder={input?.placeholder ?? ""}
        style={{
          width: inputType === "checkbox" ? 20 : "100%",
          height: inputType === "checkbox" ? 20 : 36,
          flexGrow: 0,
          flexShrink: 0,
          marginRight: 20,
          maxWidth: inputType === "checkbox" ? 20 : 240,
        }}
      />
      <div>
        <label>
          {labelText}
          {link && (
            <a
              href={linkHref}
              target="_blank"
              rel="noreferrer"
              style={{ whiteSpace: "pre" }}
            >
              {linkText}
            </a>
          )}
        </label>{" "}
        <small style={{ opacity: 0.5 }}>{smallText}</small>
      </div>
    </div>
  );
}
